'use client';
import { NavItemConfig } from '@/app/constants/nav';
import BottomNavigation from '@mui/material/BottomNavigation';
import Link from 'next/link';
import { BottomNavigationAction, Typography } from '@mui/material';
import React from 'react';
import { isNavItemActive } from '@/app/lib/logger/is-nav-item-active';
import { navIcons } from '../NavIcon';
import theme from '@/app/theme';
import { useUser } from '@/app/hooks/useUser';
import { useRouter } from 'next/navigation';
import utilsService from '@/app/helpers/utilsService';

interface NavItemProps extends Omit<NavItemConfig, 'items'> {
    pathname: string;
    onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    itemKey: string;
}
export default function RenderNavItems({ items = [], pathname }: { items?: NavItemConfig[]; pathname: string }): React.JSX.Element {
    const { user } = useUser();
    const router = useRouter();
    const handleNavItemClick = (key: string, event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (['writing'].includes(key)) {
            event.preventDefault();
            if (!user?.id) {
                router.push(utilsService.buildLoginUrl('/writing'));
                return;
            } else {
                router.push('/writing', { scroll: false });
            }
        }
        // Redirect to login if not authenticated for mychannel
        if (['mychannel'].includes(key)) {
            event.preventDefault();
            if (!user?.id) {
                router.push(utilsService.buildLoginUrl('/mychannel/profile'));
                return;
            } else {
                router.push('/mychannel/profile', { scroll: false });
            }
        }
    };

    const children = items?.map((item: NavItemConfig) => {
        const { key, label, ...rest } = item;
        return (
            <NavItem
                key={key}
                itemKey={key}
                pathname={pathname}
                label={label}
                onClick={(event: any) => handleNavItemClick(key, event)}
                {...rest}
            />
        );
    });

    return (
        <BottomNavigation
            showLabels
            sx={{ width: '100%', backgroundColor: theme.palette.background.default }}
        >
            {children}
        </BottomNavigation>
    );
}

function NavItem({ disabled, external, href, icon, matcher, pathname, title, itemKey, label, onClick, ...rest }: NavItemProps): React.JSX.Element {
    const active = isNavItemActive({ disabled, external, href, matcher, pathname });
    const Icon = icon ? (active ? navIcons[`${icon}Active`] : navIcons[icon]) : null;
    return (
        <BottomNavigationAction
            showLabel={itemKey !== 'writing'}
            component={Link}
            href={href}
            label={
                itemKey === 'writing' ? '' : (
                    <Typography
                        variant="h2_10Medium"
                        color={active ? "#2073F6" : "grayscale.gray07"}
                        sx={{
                            mt: 1,
                            lineHeight: '100%',
                        }}
                    >
                        {label}
                    </Typography>
                )
            }
            value={itemKey}
            icon={Icon ? <Icon /> : null}
            onClick={onClick}
            sx={{
                ...(active && { bgcolor: '' }),
                px: 0,
                minWidth: 'calc(360px / 8)',
                '& .MuiBottomNavigationAction-root': {
                    padding: 0,
                },
                '& .MuiBottomNavigationAction-label': {
                    marginTop: '5px',
                    lineHeight: '100%',
                },
                ...(itemKey === 'writing' && {
                    '& svg': {
                        transform: 'scale(1.2)',
                        transformOrigin: 'center',
                    },
                }),
            }}
        />
    );
}
